export interface AturanPakaiInput {
  kata_kerja?: string | null;
  label_signa?: string | null;
  label_interval?: string | null;
  is_prn?: boolean | number | string | null;
  signa_bebas?: string | null;
  dosis_qty?: number | string | null;
  satuan_dosis?: string | null;
  aturan_tambahan?: string | Array<string | null> | null;
}

export interface KomponenRacikan {
  gnr?: string | null;
  nama?: string | null;
  dosis_qty?: number | string | null;
  satuan?: string | null;
}

const BATAS_ANGKA = 1000000000;
const PECAHAN_UNICODE: { [simbol: string]: number } = {
  "½": 0.5,
  "¼": 0.25,
  "¾": 0.75
};

function teks(nilai: unknown): string {
  if (typeof nilai !== "string") {
    return "";
  }

  return nilai
    .replace(/[ \t\r\n\u00A0]+/g, " ")
    .replace(/\b(?:null|undefined|nan)\b/gi, " ")
    .replace(/ {2,}/g, " ")
    .replace(/^ | $/g, "");
}

function gabung(bagian: string[]): string {
  return bagian.filter(isi => isi !== "").join(" ");
}

function memuat(teksUtuh: string, cari: string): boolean {
  return teksUtuh.toLowerCase().indexOf(cari.toLowerCase()) !== -1;
}

export function parseAngka(nilai: unknown): number | null {
  let angka: number | null = null;

  if (typeof nilai === "number") {
    angka = nilai;
  } else if (typeof nilai === "string") {
    const isi = nilai.replace(/[ \t\r\n\u00A0]+/g, " ").replace(/^ | $/g, "");
    const pecahan = /^(\d{1,6}) ?\/ ?(\d{1,6})$/.exec(isi);
    const campuran = /^(\d{1,6}) (\d{1,6})\/(\d{1,6})$/.exec(isi);

    if (Object.prototype.hasOwnProperty.call(PECAHAN_UNICODE, isi)) {
      angka = PECAHAN_UNICODE[isi];
    } else if (pecahan) {
      const penyebut = parseInt(pecahan[2], 10);
      angka = penyebut > 0 ? parseInt(pecahan[1], 10) / penyebut : null;
    } else if (campuran) {
      const penyebut = parseInt(campuran[3], 10);
      angka =
        penyebut > 0
          ? parseInt(campuran[1], 10) + parseInt(campuran[2], 10) / penyebut
          : null;
    } else if (/^\d{1,10}(?:[.,]\d{1,6})?$/.test(isi)) {
      angka = parseFloat(isi.replace(",", "."));
    }
  }

  if (angka === null || !isFinite(angka) || angka < 0 || angka > BATAS_ANGKA) {
    return null;
  }

  return angka;
}

export function formatAngka(nilai: unknown): string {
  const angka = parseAngka(nilai);

  if (angka === null) {
    return "";
  }

  const seperseribu = Math.floor(angka * 1000 + 0.5);
  const bulat = Math.floor(seperseribu / 1000);
  let pecahan = String(seperseribu - bulat * 1000);

  while (pecahan.length < 3) {
    pecahan = "0" + pecahan;
  }

  pecahan = pecahan.replace(/0+$/, "");

  return pecahan === "" ? String(bulat) : String(bulat) + "," + pecahan;
}

function dosis(qty: unknown, satuan: unknown, pemisah: string): string {
  const angka = parseAngka(qty);

  if (angka === null || angka <= 0) {
    return "";
  }

  const satuanTeks = teks(satuan);

  return satuanTeks === ""
    ? formatAngka(angka)
    : formatAngka(angka) + pemisah + satuanTeks;
}

function daftarTambahan(nilai: unknown): string[] {
  if (typeof nilai === "string") {
    return [teks(nilai)];
  }

  if (!Array.isArray(nilai)) {
    return [];
  }

  return nilai.map(isi => teks(isi));
}

function isPrn(nilai: unknown): boolean {
  return nilai === true || nilai === 1 || nilai === "1";
}

export function formatAturanPakai(
  input: AturanPakaiInput | null | undefined
): string {
  const data: AturanPakaiInput =
    input && typeof input === "object" ? input : {};
  const signaBebas = teks(data.signa_bebas);
  const bagian: string[] = [teks(data.kata_kerja)];

  if (signaBebas !== "") {
    bagian.push(signaBebas);
  } else {
    bagian.push(teks(data.label_signa));
    bagian.push(dosis(data.dosis_qty, data.satuan_dosis, " "));

    if (isPrn(data.is_prn)) {
      if (!memuat(gabung(bagian), "bila perlu")) {
        bagian.push("bila perlu");
      }
    } else {
      bagian.push(teks(data.label_interval));
    }
  }

  const dasar = gabung(bagian);
  const tambahan: string[] = [];

  daftarTambahan(data.aturan_tambahan).forEach(isi => {
    if (isi === "" || memuat(dasar, isi)) {
      return;
    }

    const sudahAda = tambahan.some(
      ada => ada.toLowerCase() === isi.toLowerCase()
    );

    if (!sudahAda) {
      tambahan.push(isi);
    }
  });

  return gabung([dasar, tambahan.join(", ")]);
}

export function formatNamaRacikan(
  nomor: unknown,
  komponen: KomponenRacikan[] | null | undefined
): string {
  const nomorTeks =
    typeof nomor === "number" ? formatAngka(nomor) : teks(nomor);
  const label = gabung(["Racikan", nomorTeks]);
  const daftar = Array.isArray(komponen) ? komponen : [];
  const bagian: string[] = [];

  daftar.forEach(item => {
    if (!item || typeof item !== "object") {
      return;
    }

    const nama = teks(item.gnr) !== "" ? teks(item.gnr) : teks(item.nama);
    const isi = gabung([nama, dosis(item.dosis_qty, item.satuan, "")]);

    if (isi !== "") {
      bagian.push(isi);
    }
  });

  return bagian.length > 0 ? label + " (" + bagian.join("; ") + ")" : label;
}
